import { Button, Card } from "@platform/ui";
import { ErrorText, errorKey, Field, useSession } from "@platform/ui/features";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { formatDateTime } from "../lib/i18n";

type ProblemKind = "hash_mismatch" | "missing_root" | "fork" | "unlinked";

interface AuditEventRow {
  readonly id: string;
  readonly occurredAt: string;
  readonly actorType: "user" | "operator" | "system";
  readonly actorId: string | null;
  readonly tenantIds: readonly string[];
  readonly entityType: string;
  readonly entityId: string;
  readonly action: string;
  readonly before?: unknown;
  readonly after?: unknown;
}

interface Run {
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly entitiesChecked: number;
  readonly eventsChecked: number;
  readonly breaks: readonly { entityType: string; entityId: string; eventId: string; kind: ProblemKind }[];
}

interface Verdict {
  readonly events: number;
  readonly intact: boolean;
  readonly problem: { readonly eventId: string; readonly kind: ProblemKind } | null;
}

const FILTERS = ["entityType", "entityId", "actorId", "action", "tenantId"] as const;
type Filters = Record<(typeof FILTERS)[number] | "from" | "to", string>;
const EMPTY: Filters = {
  entityType: "",
  entityId: "",
  actorId: "",
  action: "",
  tenantId: "",
  from: "",
  to: "",
};

/** The query the API takes: blank filters left out, local date-times sent as UTC. */
function queryOf(filters: Filters) {
  const query: Record<string, string> = {};
  for (const key of FILTERS) if (filters[key].trim() !== "") query[key] = filters[key].trim();
  if (filters.from !== "") query.from = new Date(filters.from).toISOString();
  if (filters.to !== "") query.to = new Date(filters.to).toISOString();
  return query;
}

const json = (value: unknown) => JSON.stringify(value ?? null, null, 2);

/** The audit trail (AUD-1): the nightly chain check, events by filter, and an on-demand check of one chain. */
export function AuditPage() {
  const t = useTranslations();
  const { session } = useSession();
  const [run, setRun] = useState<Run | null | undefined>();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [rows, setRows] = useState<readonly AuditEventRow[] | undefined>();
  const [cursor, setCursor] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<Verdict | undefined>();
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(
    async (next: Filters, after: string | null) => {
      setError(undefined);
      const { data, error: problem } = await session.api.GET("/v1/ops/audit-events", {
        params: { query: { ...queryOf(next), ...(after === null ? {} : { cursor: after }) } },
      });
      if (data === undefined) return setError(t(errorKey(problem)));
      setRows((current) => (after === null ? data.items : [...(current ?? []), ...data.items]));
      setCursor(data.nextCursor);
    },
    [session, t],
  );

  useEffect(() => {
    void (async () => {
      const { data } = await session.api.GET("/v1/ops/audit-verification");
      setRun(data?.run ?? null);
    })();
    void load(EMPTY, null);
  }, [session, load]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    setApplied(filters);
    setVerdict(undefined);
    void load(filters, null);
  };

  const showEntity = (entityType: string, entityId: string) => {
    const next = { ...EMPTY, entityType, entityId };
    setFilters(next);
    setApplied(next);
    setVerdict(undefined);
    void load(next, null);
  };

  const verify = async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/audit-chains/{entityType}/{entityId}", {
      params: { path: { entityType: applied.entityType.trim(), entityId: applied.entityId.trim() } },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setVerdict(data);
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-title font-semibold text-ink">{t("admin.audit.title")}</h1>
      {run !== undefined && (
        <Card className="flex flex-col gap-2 p-4 text-small">
          {run === null ? (
            <p>{t("admin.audit.neverRun")}</p>
          ) : run.finishedAt === null ? (
            <p>{t("admin.audit.running", { date: formatDateTime(run.startedAt) })}</p>
          ) : (
            <>
              <p>
                {t("admin.audit.latestRun", {
                  date: formatDateTime(run.finishedAt),
                  entities: run.entitiesChecked,
                  events: run.eventsChecked,
                })}
              </p>
              {run.breaks.length === 0 ? (
                <p className="text-tone-done-fg">{t("admin.audit.intact")}</p>
              ) : (
                <>
                  <p role="alert" className="font-medium text-tone-problem-fg">
                    {t("admin.audit.breaks", { count: run.breaks.length })}
                  </p>
                  <ul className="flex flex-col gap-1">
                    {run.breaks.map((item) => (
                      <li key={`${item.entityType}:${item.entityId}`} className="flex items-center gap-2">
                        <span className="font-mono text-caption">{`${item.entityType} ${item.entityId}`}</span>
                        <span className="text-ink-muted">{t(`admin.audit.kind.${item.kind}`)}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => showEntity(item.entityType, item.entityId)}
                        >
                          {t("admin.audit.showEntity")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </Card>
      )}

      <form className="grid grid-cols-1 gap-3 sm:grid-cols-4" onSubmit={search}>
        {FILTERS.map((key) => (
          <Field
            key={key}
            id={`audit-${key}`}
            label={t(`admin.audit.${key}`)}
            value={filters[key]}
            onChange={(event) => setFilters({ ...filters, [key]: event.target.value })}
          />
        ))}
        {(["from", "to"] as const).map((key) => (
          <Field
            key={key}
            id={`audit-${key}`}
            type="datetime-local"
            label={t(`admin.audit.${key}`)}
            value={filters[key]}
            onChange={(event) => setFilters({ ...filters, [key]: event.target.value })}
          />
        ))}
        <div className="flex items-end">
          <Button type="submit">{t("admin.audit.search")}</Button>
        </div>
      </form>

      {applied.entityType.trim() !== "" && applied.entityId.trim() !== "" && (
        <div className="flex items-center gap-3 text-small">
          <Button variant="secondary" onClick={() => void verify()}>
            {t("admin.audit.verifyChain")}
          </Button>
          {verdict !== undefined &&
            (verdict.problem === null ? (
              <span className="text-tone-done-fg">
                {t("admin.audit.chainIntact", { events: verdict.events })}
              </span>
            ) : (
              <span role="alert" className="text-tone-problem-fg">
                {t("admin.audit.chainBroken", {
                  eventId: verdict.problem.eventId,
                  kind: t(`admin.audit.kind.${verdict.problem.kind}`),
                })}
              </span>
            ))}
        </div>
      )}

      {error !== undefined && <ErrorText>{error}</ErrorText>}
      {rows?.length === 0 && <p className="text-small text-ink-muted">{t("admin.audit.none")}</p>}
      {rows !== undefined && rows.length > 0 && (
        <ul className="flex flex-col divide-y divide-border-subtle overflow-hidden rounded-(--radius-card) border border-border bg-surface text-small">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-col gap-1 px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-caption font-semibold text-ink">{row.action}</span>
                <span className="text-caption text-ink-muted tabular-nums">
                  {formatDateTime(row.occurredAt)}
                </span>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-ink-muted">
                  {t("admin.audit.actorLine", {
                    type: t(`admin.audit.actorType.${row.actorType}`),
                    id: row.actorId ?? "",
                  })}
                </span>
                <button
                  type="button"
                  className="font-mono text-caption text-brand-600 hover:text-brand-700"
                  onClick={() => showEntity(row.entityType, row.entityId)}
                >
                  {`${row.entityType} ${row.entityId}`}
                </button>
              </div>
              <details>
                <summary className="cursor-pointer text-caption text-ink-muted">
                  {t("admin.audit.details")}
                </summary>
                <div className="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2">
                  <div>
                    <p className="text-caption text-ink-muted">{t("admin.audit.before")}</p>
                    <pre className="overflow-x-auto rounded-(--radius-control) bg-subtle p-2 text-caption">
                      {json(row.before)}
                    </pre>
                  </div>
                  <div>
                    <p className="text-caption text-ink-muted">{t("admin.audit.after")}</p>
                    <pre className="overflow-x-auto rounded-(--radius-control) bg-subtle p-2 text-caption">
                      {json(row.after)}
                    </pre>
                  </div>
                </div>
              </details>
            </li>
          ))}
        </ul>
      )}
      {cursor !== null && (
        <Button variant="secondary" className="self-center" onClick={() => void load(applied, cursor)}>
          {t("admin.audit.more")}
        </Button>
      )}
    </div>
  );
}
